// ═══════════════════════════════════════════════════════════════
// Settings — Users Tab
// User-role assignment management with Ignatius user integration
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Modal, ActionIcon, Stack, Table, ScrollArea, Divider, PasswordInput,
  Switch, Tabs, Loader, Center,
} from '../../components/gab/GabComponents';
import { IconSearch, IconPlus, IconPencil, IconTrash, IconUserPlus, IconUsers } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useRoleStore } from '../../store/roleStore';
import { useAuthStore } from '../../store/authStore';
import { IconKey } from '@tabler/icons-react';
import { fetchIgnatiusUsers, validateEmail, createIgnatiusUser, resetUserPassword } from '../../lib/api-switch';
import type { IgnatiusUser } from '../../lib/api-switch';
import type { UserRole } from '../../types/rbac';

interface AssignFormState {
  userEmail: string;
  userName: string;
  roleId: string | null;
}

interface CreateUserForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  role: string;
  isExternal: boolean;
}

interface ResetPwState {
  email: string;
  name: string;
  newPassword: string;
}

const EMPTY_ASSIGN: AssignFormState = { userEmail: '', userName: '', roleId: null };
const EMPTY_CREATE: CreateUserForm = {
  firstName: '', lastName: '', email: '', phone: '',
  password: '', role: 'user', isExternal: false,
};

export default function UsersTab() {
  const { roles, userRoles, loading, loadRoles, loadUserRoles, assignRole, removeAssignment } = useRoleStore();

  // Ignatius users
  const [igUsers, setIgUsers] = useState<IgnatiusUser[]>([]);
  const [igLoading, setIgLoading] = useState(false);

  // UI state
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<UserRole | null>(null);
  const [form, setForm] = useState<AssignFormState>(EMPTY_ASSIGN);
  const [deleteTarget, setDeleteTarget] = useState<UserRole | null>(null);
  const [resetPw, setResetPw] = useState<ResetPwState | null>(null);
  const [resettingPw, setResettingPw] = useState(false);
  const [saving, setSaving] = useState(false);

  // Create user modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadRoles();
    loadUserRoles();
    loadIgnatiusUsers();
  }, []);

  const loadIgnatiusUsers = async () => {
    setIgLoading(true);
    try {
      const users = await fetchIgnatiusUsers();
      setIgUsers(users);
    } catch {
      // Silently fail — manual email entry still works
    } finally {
      setIgLoading(false);
    }
  };

  const roleOptions = useMemo(
    () => roles.map(r => ({ value: String(r.id), label: r.roleName })),
    [roles],
  );

  // Ignatius users as searchable options for the assign modal
  const igUserOptions = useMemo(
    () => igUsers.map(u => ({
      value: u.email,
      label: `${u.fullName} (${u.email})`,
    })),
    [igUsers],
  );

  const filtered = useMemo(() => {
    if (!search) return userRoles;
    const q = search.toLowerCase();
    return userRoles.filter(
      ur => (ur.userEmail || '').toLowerCase().includes(q) || (ur.userName || '').toLowerCase().includes(q),
    );
  }, [userRoles, search]);

  // Users who have Ignatius accounts but no OpenGov EMC role assignment
  const unassignedUsers = useMemo(() => {
    const assignedEmails = new Set(userRoles.map(ur => (ur.userEmail || '').toLowerCase()));
    return igUsers.filter(u => !assignedEmails.has(u.email.toLowerCase()));
  }, [igUsers, userRoles]);

  const openAssign = (prefillEmail?: string, prefillName?: string) => {
    setEditingAssignment(null);
    setForm({
      userEmail: prefillEmail ?? '',
      userName: prefillName ?? '',
      roleId: null,
    });
    setModalOpen(true);
  };

  const openChangeRole = (ur: UserRole) => {
    setEditingAssignment(ur);
    setForm({ userEmail: ur.userEmail, userName: ur.userName, roleId: ur.roleId });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.userEmail.trim() || !form.roleId) {
      notifications.show({ message: 'Email and role are required', color: 'red' });
      return;
    }
    const selectedRole = roles.find(r => String(r.id) === form.roleId);
    if (!selectedRole) {
      notifications.show({ message: 'Invalid role selected', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      await assignRole(form.userEmail, form.userName, String(selectedRole.id), selectedRole.roleName);
      notifications.show({
        message: editingAssignment ? 'Role updated' : 'Role assigned',
        color: 'green',
      });
      setModalOpen(false);
    } catch {
      notifications.show({ message: 'Failed to save assignment', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!deleteTarget) return;
    try {
      await removeAssignment(deleteTarget.id);
      notifications.show({ message: `Assignment for ${deleteTarget.userEmail} removed`, color: 'gray' });
      setDeleteTarget(null);
    } catch {
      notifications.show({ message: 'Failed to remove assignment', color: 'red' });
    }
  };

  const handleCreateUser = async () => {
    const { firstName, lastName, email, password } = createForm;
    if (!firstName || !lastName || !email || !password) {
      notifications.show({ message: 'First name, last name, email, and password are required', color: 'red' });
      return;
    }
    setCreating(true);
    try {
      // Validate email availability
      await validateEmail(email);

      // Create the user
      await createIgnatiusUser({
        firstName,
        lastName,
        email,
        role: createForm.role,
      } as any);

      notifications.show({ message: `Account created for ${email}`, color: 'green' });
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);

      // Refresh user list
      await loadIgnatiusUsers();
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Failed to create user',
        color: 'red',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Users</Title>
        <Group gap="xs">
          <Button leftSection={<IconUserPlus size={14} />} size="xs" variant="light" color="green" onClick={() => setCreateOpen(true)}>
            Add User
          </Button>
          <Button leftSection={<IconPlus size={14} />} size="xs" color="#64748B" onClick={() => openAssign()}>
            Assign Role
          </Button>
        </Group>
      </Group>

      <Tabs defaultValue="assigned">
        <Tabs.List mb="md">
          <Tabs.Tab value="assigned" leftSection={<IconUsers size={14} />}>
            Assigned ({userRoles.length})
          </Tabs.Tab>
          <Tabs.Tab value="unassigned" leftSection={<IconUserPlus size={14} />}>
            Unassigned ({unassignedUsers.length})
          </Tabs.Tab>
        </Tabs.List>

        {/* ── Assigned Users Tab ─────────────────────────────────── */}
        <Tabs.Panel value="assigned">
          <TextInput
            placeholder="Search by email or name..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            mb="md"
            style={{ maxWidth: 360 }}
          />

          <Paper withBorder>
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th>Assigned By</Table.Th>
                    <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filtered.length === 0 && !loading && (
                    <Table.Tr>
                      <Table.Td colSpan={5}>
                        <Text c="dimmed" ta="center" py="lg">No user-role assignments found</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {filtered.map(ur => (
                    <Table.Tr key={ur.id}>
                      <Table.Td>{ur.userEmail}</Table.Td>
                      <Table.Td>{ur.userName || <Text c="dimmed" span>--</Text>}</Table.Td>
                      <Table.Td>
                        <Badge color="#64748B" variant="light" size="sm">{ur.roleName || 'Unknown'}</Badge>
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{ur.assignedBy || '--'}</Text></Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <ActionIcon variant="subtle" color="orange" size="sm" title="Reset Password"
                            onClick={() => setResetPw({ email: ur.userEmail, name: ur.userName, newPassword: '' })}>
                            <IconKey size={14} />
                          </ActionIcon>
                          <ActionIcon variant="subtle" color="blue" size="sm" onClick={() => openChangeRole(ur)}>
                            <IconPencil size={14} />
                          </ActionIcon>
                          <ActionIcon variant="subtle" color="red" size="sm" onClick={() => setDeleteTarget(ur)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        </Tabs.Panel>

        {/* ── Unassigned Users Tab ───────────────────────────────── */}
        <Tabs.Panel value="unassigned">
          {igLoading ? (
            <Center py="xl"><Loader size="sm" /></Center>
          ) : unassignedUsers.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">All Ignatius users have been assigned OpenGov EMC roles</Text>
          ) : (
            <Paper withBorder>
              <ScrollArea>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Ignatius Role</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Action</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {unassignedUsers.map(u => (
                      <Table.Tr key={u.email}>
                        <Table.Td>{u.email}</Table.Td>
                        <Table.Td>{u.fullName}</Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm" color={u.isAdmin ? 'red' : 'gray'}>
                            {u.topLevelRole}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="dot" color={u.active ? 'green' : 'gray'} size="sm">
                            {u.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Button
                            size="xs"
                            variant="light"
                            color="#64748B"
                            onClick={() => openAssign(u.email, u.fullName)}
                          >
                            Assign
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* ── Assign / Change Role Modal ────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingAssignment ? 'Change Role' : 'Assign Role'}
        size="md"
      >
        <Stack gap="sm">
          {editingAssignment ? (
            <TextInput label="User Email" value={form.userEmail} disabled />
          ) : igUsers.length > 0 ? (
            <Select
              label="User"
              required
              searchable
              data={igUserOptions}
              value={form.userEmail}
              onChange={val => {
                const u = igUsers.find(u => u.email === val);
                setForm(prev => ({
                  ...prev,
                  userEmail: val ?? '',
                  userName: u?.fullName ?? prev.userName,
                }));
              }}
              placeholder="Search by name or email..."
              nothingFoundMessage="No users found"
            />
          ) : (
            <TextInput
              label="User Email"
              required
              value={form.userEmail}
              onChange={e => setForm(prev => ({ ...prev, userEmail: e.target.value }))}
            />
          )}
          <TextInput
            label="User Name"
            value={form.userName}
            onChange={e => setForm(prev => ({ ...prev, userName: e.target.value }))}
          />
          <Select
            label="Role"
            required
            data={roleOptions}
            value={form.roleId}
            onChange={val => setForm(prev => ({ ...prev, roleId: val }))}
            placeholder="Select a role..."
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="#64748B" loading={saving} onClick={handleSave}>
              {editingAssignment ? 'Update' : 'Assign'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Create User Modal ────────────────────────────────────── */}
      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Ignatius User"
        size="md"
      >
        <Stack gap="sm">
          <Group grow>
            <TextInput
              label="First Name"
              required
              value={createForm.firstName}
              onChange={e => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
            />
            <TextInput
              label="Last Name"
              required
              value={createForm.lastName}
              onChange={e => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
            />
          </Group>
          <TextInput
            label="Email"
            required
            autoComplete="off"
            value={createForm.email}
            onChange={e => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
          />
          <TextInput
            label="Phone"
            autoComplete="off"
            value={createForm.phone}
            onChange={e => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
          />
          <PasswordInput
            label="Password"
            required
            autoComplete="new-password"
            value={createForm.password}
            onChange={e => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
          />
          <Divider />
          <Select
            label="Ignatius Role"
            data={[
              { value: 'user', label: 'User' },
              { value: 'admin', label: 'Admin' },
            ]}
            value={createForm.role}
            onChange={val => setCreateForm(prev => ({ ...prev, role: val ?? 'User' }))}
          />
          <Switch
            label="External User"
            description="External volunteers and business owners"
            checked={createForm.isExternal}
            onChange={e => setCreateForm(prev => ({ ...prev, isExternal: e.currentTarget.checked }))}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button color="green" loading={creating} onClick={handleCreateUser}>
              Create Account
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Remove Confirmation ───────────────────────────────── */}
      <Modal
        opened={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Remove Assignment"
        size="sm"
      >
        <Text mb="md">
          Remove role assignment for <strong>{deleteTarget?.userEmail}</strong>?
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleRemove}>Remove</Button>
        </Group>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        opened={resetPw !== null}
        onClose={() => setResetPw(null)}
        title="Reset Password"
        size="sm"
      >
        {resetPw && (
          <Stack>
            <Text size="sm">
              Reset password for <strong>{resetPw.name || resetPw.email}</strong>
            </Text>
            <Text size="xs" c="dimmed">{resetPw.email}</Text>
            <PasswordInput
              label="New Password"
              autoComplete="new-password"
              value={resetPw.newPassword}
              onChange={e => setResetPw(prev => prev ? { ...prev, newPassword: e.target.value } : null)}
              placeholder="Enter new password"
              autoFocus
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setResetPw(null)}>Cancel</Button>
              <Button
                color="orange"
                loading={resettingPw}
                disabled={!resetPw.newPassword || resetPw.newPassword.length < 6}
                onClick={async () => {
                  setResettingPw(true);
                  try {
                    const adminToken = useAuthStore.getState().token;
                    if (!adminToken) throw new Error('Not authenticated');
                    await resetUserPassword(resetPw.email, resetPw.newPassword, adminToken);
                    notifications.show({
                      title: 'Password Reset',
                      message: `Password for ${resetPw.email} has been reset`,
                      color: 'green',
                    });
                    setResetPw(null);
                  } catch (err) {
                    notifications.show({
                      title: 'Reset Failed',
                      message: err instanceof Error ? err.message : 'Failed to reset password',
                      color: 'red',
                    });
                  } finally {
                    setResettingPw(false);
                  }
                }}
              >
                Reset Password
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
